import { c as _c } from "react/compiler-runtime";
import { useCallback } from 'react';
import { pattern } from '@endurancewarranty/lib';
import { useAppRouter } from '@hooks/navigation';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { AppRouteEnum, CallDirectionEnum, ChannelEnum, NavigationSourceQueryEnum, PersistedStateKeyEnum } from '@lib/enums';
import { quoteWorkflowSchema } from '@quotes/_workflow/schema';
import { useJotaiValue } from '@states/jotai/hooks';
import { contactSelector } from '@states/softphone/contactSelector';
import { currentCallAtom } from '@states/softphone/currentCallAtom';
import type { TSalesWorkflow } from '@hooks/workflows';
import type { TQuote } from '@models';
const {
  match,
  P
} = pattern;
export type TUseContactRoutingReturn = {
  goToWaitingPageForNextContact: () => void;
  goToSearchPageForCurrentContact: (options?: GoToSearchPageForCurrentContactOptions) => void;
  goToWrapUpCall: (quoteId?: Maybe<string>, consumerId?: Maybe<string>, replace?: boolean) => void;
  goToQuoteFlowEntry: (quoteId: string) => void;
  goToInProgressQuote: (workflow: TSalesWorkflow, quote: TQuote, replace?: boolean) => void;
  goToContactConfirmation: () => void;
  goToConsumerProfile: (consumerId: string | number) => void;
  goToConsumerVehicle: (consumerId: number, consumerVehicleId: number) => void;
  goToMoxyCustomerNumberSearch: () => void;
  goToDashboard: () => void;
  goToContactPreview: () => void;
  goToContractDetails: (contractId: number) => void;
};
type GoToSearchPageForCurrentContactOptions = {
  clearPreviousSearchResults?: boolean;
  replace?: boolean;
};
export function useContactRouter() {
  const $ = _c(46);
  const router = useAppRouter();
  const {
    isCustomerService,
    isRetention
  } = useCurrentUser();
  const {
    contactId,
    channel,
    callDirection,
    isTransfer
  } = useJotaiValue(contactSelector);
  const {
    lastTouchedQuoteId,
    lastTouchedConsumerId
  } = useJotaiValue(currentCallAtom);
  let t0;
  if ($[0] !== router) {
    t0 = consumerId => {
      router.build(AppRouteEnum.ConsumerProfile).setId(consumerId).go();
    };
    $[0] = router;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const goToConsumerProfile = t0;
  let t1;
  if ($[2] !== router) {
    t1 = (consumerId_0, consumerVehicleId) => {
      router.build(AppRouteEnum.ConsumerVehicle).setId(consumerId_0).setId(consumerVehicleId).go();
    };
    $[2] = router;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const goToConsumerVehicle = t1;
  let t2;
  if ($[4] !== router) {
    t2 = () => {
      router.build(AppRouteEnum.ContactConfirmation).go();
    };
    $[4] = router;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const goToContactConfirmation = t2;
  let t3;
  if ($[6] !== router) {
    t3 = () => {
      router.build(AppRouteEnum.Dashboard).go();
    };
    $[6] = router;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const goToDashboard = t3;
  let t4;
  if ($[8] !== router) {
    t4 = () => {
      router.build(AppRouteEnum.ContactSearchMoxyCustomerNumber).go();
    };
    $[8] = router;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const goToMoxyCustomerNumberSearch = t4;
  let t5;
  if ($[10] !== router) {
    t5 = quoteId => {
      router.build(AppRouteEnum.QuoteFlowEntry).setId(quoteId).go();
    };
    $[10] = router;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const goToQuoteFlowEntry = t5;
  let t6;
  if ($[12] !== router) {
    t6 = () => {
      router.build(AppRouteEnum.ContactWaiting).go();
    };
    $[12] = router;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const goToWaitingPageForNextContact = t6;
  let t7;
  if ($[14] !== router) {
    t7 = () => {
      router.build(AppRouteEnum.ContactPreview).go();
    };
    $[14] = router;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const goToContactPreview = t7;
  let t8;
  if ($[16] !== callDirection || $[17] !== channel || $[18] !== goToMoxyCustomerNumberSearch || $[19] !== isCustomerService || $[20] !== isRetention || $[21] !== isTransfer || $[22] !== router) {
    t8 = options => {
      const clearPreviousSearchResults = options?.clearPreviousSearchResults ?? true;
      const routingConfig = options?.replace != null ? {
        replace: options.replace
      } : undefined;
      if (clearPreviousSearchResults) {
        localStorage.removeItem(PersistedStateKeyEnum.DirectMailSearchType);
        localStorage.removeItem(PersistedStateKeyEnum.DirectMailSearchFormState);
        localStorage.removeItem(PersistedStateKeyEnum.ManualSearchFormState);
        localStorage.removeItem(PersistedStateKeyEnum.SearchType);
      }
      if (isCustomerService) {
        router.build(AppRouteEnum.Dashboard).go();
        return;
      }
      if (isRetention) {
        router.build(AppRouteEnum.ContactSearchAuto).go(routingConfig);
        return;
      }
      if (isTransfer === true) {
        goToMoxyCustomerNumberSearch();
        return;
      }
      if (channel === ChannelEnum.DirectMail && callDirection === CallDirectionEnum.Inbound) {
        router.build(AppRouteEnum.ContactSearchMail).go(routingConfig);
        return;
      }
      router.build(AppRouteEnum.ContactSearchAuto).go(routingConfig);
    };
    $[16] = callDirection;
    $[17] = channel;
    $[18] = goToMoxyCustomerNumberSearch;
    $[19] = isCustomerService;
    $[20] = isRetention;
    $[21] = isTransfer;
    $[22] = router;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const goToSearchPageForCurrentContact = t8;
  let t9;
  if ($[24] !== contactId || $[25] !== lastTouchedConsumerId || $[26] !== lastTouchedQuoteId || $[27] !== router) {
    t9 = (quoteId_0, consumerId_1, t10) => {
      const replace = t10 === undefined ? false : t10;
      return match({
        consumerId: consumerId_1,
        contactId,
        lastTouchedConsumerId,
        lastTouchedQuoteId,
        quoteId: quoteId_0
      }).with({
        quoteId: P.nonNullable
      }, () => router.build(AppRouteEnum.QuoteFlowEntry).setId(quoteId_0).setQuery({
        source: NavigationSourceQueryEnum.WrapCall
      }).go({
        replace
      })).with({
        lastTouchedQuoteId: P.nonNullable,
        lastTouchedConsumerId: P.nonNullable,
        consumerId: lastTouchedConsumerId?.toString()
      }, () => router.build(AppRouteEnum.QuoteFlowEntry).setId(lastTouchedQuoteId).setQuery({
        source: NavigationSourceQueryEnum.WrapCall
      }).go({
        replace
      })).with({
        consumerId: P.nonNullable
      }, () => router.build(AppRouteEnum.ConsumerWrapUp).setId(consumerId_1).go({
        replace
      })).with({
        contactId: P.nonNullable
      }, () => router.build(AppRouteEnum.ContactWrapUp).setId(contactId).go({
        replace
      })).otherwise(_temp);
    };
    $[24] = contactId;
    $[25] = lastTouchedConsumerId;
    $[26] = lastTouchedQuoteId;
    $[27] = router;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const goToWrapUpCall = t9;
  let t10;
  if ($[29] !== router) {
    t10 = (workflow, quote, t11) => {
      const replace_0 = t11 === undefined ? false : t11;
      const firstStep = workflow.steps.at(0);
      const firstStepDefinition = quoteWorkflowSchema[firstStep.step];
      router.build(firstStepDefinition.slug).setId(quote.id).go({
        replace: replace_0
      });
    };
    $[29] = router;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const goToInProgressQuote = t10;
  let t11;
  if ($[31] !== router) {
    t11 = contractId => {
      const builder = router.build(AppRouteEnum.ContractById).setId(contractId);
      builder.go();
    };
    $[31] = router;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const goToContractDetails = t11;
  let t12;
  if ($[33] !== goToConsumerProfile || $[34] !== goToConsumerVehicle || $[35] !== goToContactConfirmation || $[36] !== goToContactPreview || $[37] !== goToContractDetails || $[38] !== goToDashboard || $[39] !== goToInProgressQuote || $[40] !== goToMoxyCustomerNumberSearch || $[41] !== goToQuoteFlowEntry || $[42] !== goToSearchPageForCurrentContact || $[43] !== goToWaitingPageForNextContact || $[44] !== goToWrapUpCall) {
    t12 = {
      goToWaitingPageForNextContact,
      goToSearchPageForCurrentContact,
      goToWrapUpCall,
      goToQuoteFlowEntry,
      goToInProgressQuote,
      goToContactConfirmation,
      goToConsumerProfile,
      goToConsumerVehicle,
      goToMoxyCustomerNumberSearch,
      goToDashboard,
      goToContactPreview,
      goToContractDetails
    };
    $[33] = goToConsumerProfile;
    $[34] = goToConsumerVehicle;
    $[35] = goToContactConfirmation;
    $[36] = goToContactPreview;
    $[37] = goToContractDetails;
    $[38] = goToDashboard;
    $[39] = goToInProgressQuote;
    $[40] = goToMoxyCustomerNumberSearch;
    $[41] = goToQuoteFlowEntry;
    $[42] = goToSearchPageForCurrentContact;
    $[43] = goToWaitingPageForNextContact;
    $[44] = goToWrapUpCall;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  return t12;
}
function _temp() {
  return null;
}